import { ReactNode, useEffect } from "react";
import {
  ErrorOption,
  FormProvider,
  Path,
  SubmitErrorHandler,
  SubmitHandler,
} from "react-hook-form";

import { UseFormProps, UseFormReturn } from "./hooks/useForm/helpers/types";
import { useForm } from "./hooks/useForm/useForm";

/**
 * It's a wrapper for the react-hook-form `<FormProvider />` & `<form />`
 * components and `useForm` hook.
 *
 * The component takes the following props:
 * - `defaultValues` - default values for the form inputs
 * - `struct` - superstruct's object schema defining the form validation
 * - `onValidSubmit` - it's a function called when a valid form is submitted
 * - `onInvalidSubmit?` - it's a function called when an INVALID form is submitted
 * - `children` - render props function that takes props returned from `useForm`
 */
export function Form<T extends Record<string, unknown>>({
  struct,
  defaultValues,
  id,
  errors,
  onValidSubmit,
  onInvalidSubmit,
  children,
  className,
}: FormProps<T>) {
  const providerProps = useForm({
    struct,
    defaultValues,
  });

  const handleSubmit = providerProps.handleSubmit(
    onValidSubmit,
    onInvalidSubmit
  );

  useEffect(() => {
    if (errors) {
      errors.forEach(({ fieldName, options }) => {
        providerProps.setError(fieldName as Path<T>, options);
      });
    }
  }, [providerProps, errors]);

  return (
    <FormProvider {...providerProps}>
      <form onSubmit={handleSubmit} noValidate id={id} className={className}>
        {children(providerProps)}
      </form>
    </FormProvider>
  );
}

type FormProps<T> = {
  children: (props: UseFormReturn<T>) => ReactNode;
  onValidSubmit: SubmitHandler<T>;
  onInvalidSubmit?: SubmitErrorHandler<T>;
  id?: string;
  className?: string;
  errors?: {
    fieldName: string;
    options: ErrorOption;
  }[];
} & UseFormProps<T>;
